import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { EnrichedEnrollment, EnrollmentState, getMyEnrollments } from '../api/enrollments';
import { CycleProgress, getCycleProgress } from '../api/cycle-progress';
import { getStudents, StudentListItem } from '../api/students';
import { getLearningProgress, ProgressSnapshot } from '../api/learning';
import LearningProgressSummary from '../components/LearningProgressSummary';
import PendingConfirmations from '../components/PendingConfirmations';
import PendingReviews from '../components/PendingReviews';
import PendingLearnerRequests from '../components/PendingLearnerRequests';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import { formatMoney } from '../../../commodity/format/money';
import {
  ENROLLMENT_STATE_LABEL as STATE_LABEL,
  formatDeadline,
  REQUEST_OUTCOME_COPY,
} from '../components/enrollmentRequest';
import type { DisplayTimeZone } from '../api/learner';
import { familyZoneLabel, formatShortDateTime } from '../time/zonedTime';
import { useHouseholdTime } from '../time/useFamilyTime';

const STATE_TONE: Record<EnrollmentState, { bg: string; fg: string }> = {
  PendingPayment: { bg: '#fff8e6', fg: '#7a4d06' },
  PendingTeacherAcceptance: { bg: '#e7f5ff', fg: '#1c4f80' },
  Active: { bg: '#edf8e5', fg: '#3f7410' },
  PaymentFailed: { bg: '#ffe9e9', fg: '#8f2323' },
  TeacherDeclined: { bg: '#eef1f5', fg: '#5a6672' },
  AcceptanceExpired: { bg: '#eef1f5', fg: '#5a6672' },
  Cancelled: { bg: '#eef1f5', fg: '#5a6672' },
  Completed: { bg: '#eaf2fb', fg: '#1c4f80' },
};

const StateChip: React.FC<{ state: EnrollmentState }> = ({ state }) => {
  const tone = STATE_TONE[state] ?? STATE_TONE.Cancelled;
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        borderRadius: 4,
        padding: '3px 8px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {STATE_LABEL[state] ?? state}
    </span>
  );
};

/**
 * The next session as the server projected it (PRT-09-FR-06); never derived here.
 * A read that failed is not an empty calendar (DEC-S3-P08-07).
 */
const nextSessionLine = (
  tz: DisplayTimeZone,
  snap: ProgressSnapshot | undefined,
  unknown: boolean,
): string => {
  if (snap?.nextSession) return formatShortDateTime(snap.nextSession.scheduledStart, tz);
  return unknown || snap?.degraded ? 'No disponible por ahora' : 'Sin sesiones próximas';
};

/** Resolves to null on failure — including a mock that returns nothing at all. */
const settle = async <T,>(f: () => Promise<T>): Promise<T | null> => {
  try {
    return (await f()) ?? null;
  } catch {
    return null;
  }
};

const holdbackLine = (tz: DisplayTimeZone, cp?: CycleProgress): string | null => {
  if (!cp) return null;
  const h = cp.holdback;
  if (h.holdbackReleased) return 'Retención liberada ✅';
  if (!h.holdbackReleaseAt) return null;
  if (h.holdbackReleasable) return 'Retención lista para liberar';
  return `Retención se libera el ${formatShortDateTime(h.holdbackReleaseAt, tz)}`;
};

/** Payment never completed, so the enrollment can still be driven to Active. */
const isResumable = (state: EnrollmentState) =>
  state === 'PendingPayment' || state === 'PaymentFailed';

/**
 * The parent's home.
 *
 * Two things QA reported are fixed here. The cards used to be titled
 * "Inscripción a3f9c1b2" — a truncated UUID — because the aggregate only stores
 * IDs; the list endpoint now resolves course, child and teacher names. And an
 * enrollment whose payment never completed had no route back into checkout, so
 * it simply sat there looking broken.
 */
const ParentDashboard: React.FC = () => {
  const navigate = useNavigate();
  // Times in the household's zone, as the server resolved it — never the browser's.
  const { tz } = useHouseholdTime();

  const [enrollments, setEnrollments] = useState<EnrichedEnrollment[]>([]);
  const [progressById, setProgressById] = useState<Record<string, CycleProgress>>({});
  /** The server's learning progress snapshot per enrollment (Sprint 3). */
  const [snapshotById, setSnapshotById] = useState<Record<string, ProgressSnapshot>>({});
  /** Learners whose progress read failed: their next session is unknown, not absent. */
  const [unreadableLearners, setUnreadableLearners] = useState<ReadonlySet<string>>(new Set());
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumingId, setResumingId] = useState<string | null>(null);
  /** The students call failed: an empty list here is not an answer (S1.3). */
  const [studentsUnknown, setStudentsUnknown] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      // A failed students call used to become an empty list, and the screen
      // reasons over that: it tells the parent to "add your first student",
      // with a button, to someone who has already registered their children.
      const [{ enrollments: list }, studentsResult] = await Promise.all([
        getMyEnrollments(),
        getStudents()
          .then((value) => ({ ok: true as const, value }))
          .catch(() => ({ ok: false as const, value: [] as StudentListItem[] })),
      ]);
      setEnrollments(list ?? []);
      setStudents(studentsResult.value ?? []);
      setStudentsUnknown(!studentsResult.ok);

      // Progress is supporting detail: a failure on one enrollment must not
      // take down the whole dashboard.
      const entries = await Promise.all(
        (list ?? []).map(async (e) => {
          const cp = await getCycleProgress(e.enrollmentId).catch(() => null);
          return cp ? ([e.enrollmentId, cp] as const) : null;
        }),
      );
      setProgressById(
        Object.fromEntries(entries.filter((x): x is [string, CycleProgress] => x !== null)),
      );

      // Progress is the server's snapshot, one call per learner. A learner
      // whose snapshot cannot be read shows no progress line, and says so.
      const perStudent = await Promise.all(
        (studentsResult.value ?? []).map((st) => settle(() => getLearningProgress(st.studentId))),
      );
      const snapshots: Record<string, ProgressSnapshot> = {};
      for (const result of perStudent) {
        for (const snap of result?.snapshots ?? []) snapshots[snap.enrollmentId] = snap;
      }
      setSnapshotById(snapshots);
      setUnreadableLearners(
        new Set(
          (studentsResult.value ?? [])
            .filter((_st, i) => perStudent[i] === null)
            .map((st) => st.studentId),
        ),
      );
    } catch (err: any) {
      setError(err?.message || 'No se pudo cargar tu panel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Back through the checkout screen rather than straight to the payment
  // provider: checkout now requires the guardian to accept the recording
  // notice (MVP-01), and a pending enrollment opened before the notice existed
  // has no such acceptance. The orchestrator reuses the pending enrollment.
  const handleResume = (enrollment: EnrichedEnrollment) => {
    setResumingId(enrollment.enrollmentId);
    navigate(`/courses/${enrollment.courseId}/enroll`);
  };

  if (loading) return <p>Cargando tu panel...</p>;

  // An empty list that came from a failed load is not an answer: no
  // onboarding and no "no enrollments yet" beside a load error.
  const answered = !error && !studentsUnknown;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 4,
        }}
      >
        <h1 style={{ margin: 0, flex: '1 1 auto' }}>Mi panel</h1>
        <Link
          to="/parent/past-classes"
          className="btn btn-outline btn-sm"
          style={{ textDecoration: 'none' }}
        >
          Clases pasadas
        </Link>
        <Link
          to="/parent/students/new"
          className="btn btn-outline btn-sm"
          style={{ textDecoration: 'none' }}
        >
          Agregar alumno
        </Link>
      </div>
      <p style={{ color: '#666', marginTop: 0 }}>
        El avance de cada inscripción y sus próximas sesiones.
      </p>
      <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>{familyZoneLabel(tz)}</p>

      {error && (
        <div
          role="alert"
          style={{
            background: '#ffe9e9',
            border: '1px solid #cb4242',
            color: '#8f2323',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {studentsUnknown && (
        <div role="alert" style={{ marginBottom: 16 }}>
          <ErrorState
            action="cargar tus alumnos"
            error={new Error('Vuelve a intentarlo en un momento.')}
            onRetry={load}
            compact
          />
        </div>
      )}

      {answered && students.length === 0 && (
        <div
          style={{
            border: '1px solid #4a90e2',
            background: '#eaf2fb',
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 16,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 300px' }}>
            <div style={{ fontWeight: 700, color: '#1c4f80', marginBottom: 2 }}>
              Agrega a tu primer alumno
            </div>
            <div style={{ fontSize: 14, color: '#1c4f80' }}>
              Necesitas registrar a tu hijo o hija antes de poder inscribirlo en un curso.
            </div>
          </div>
          <Link
            to="/parent/students/new"
            className="btn btn-primary btn-sm"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            Agregar alumno
          </Link>
        </div>
      )}

      <PendingConfirmations enrollmentIds={enrollments.map((e) => e.enrollmentId)} actor="parent" />

      <PendingReviews />

      <PendingLearnerRequests />

      {answered && enrollments.length === 0 && students.length > 0 && (
        <p style={{ color: '#666' }}>
          Todavía no tienes inscripciones. <Link to="/courses">Explora los cursos</Link>.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        {enrollments.map((e) => {
          const cp = progressById[e.enrollmentId];
          const hb = holdbackLine(tz, cp);
          const resumable = isResumable(e.state);
          const busy = resumingId === e.enrollmentId;

          return (
            <div
              key={e.enrollmentId}
              style={{
                border: '1px solid #d1d5da',
                borderRadius: 8,
                padding: 16,
                background: '#fff',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 16 }}>
                    {e.courseTitle ?? `Inscripción ${e.enrollmentId.slice(0, 8)}`}
                  </strong>
                  <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                    {e.childName && <>Alumno: {e.childName}</>}
                    {e.childName && e.teacherName && ' · '}
                    {e.teacherName && <>Profesor: {e.teacherName}</>}
                    {(e.childName || e.teacherName) && ' · '}
                    {formatMoney(e.total, e.currency)}
                  </div>
                </div>
                <StateChip state={e.state} />
              </div>

              {/* D-P07-03: authorized, not charged — say so, and by when. */}
              {e.state === 'PendingTeacherAcceptance' && (
                <div
                  role="status"
                  style={{
                    background: '#e7f5ff',
                    border: '1px solid #74c0fc',
                    borderRadius: 8,
                    padding: '10px 12px',
                    margin: '12px 0',
                    fontSize: 14,
                    color: '#1c4f80',
                  }}
                >
                  <div>{REQUEST_OUTCOME_COPY.awaiting}</div>
                  <div style={{ marginTop: 4 }}>
                    El profesor tiene hasta el <strong>{formatDeadline(e.acceptBy, tz)}</strong>{' '}
                    para responder.
                  </div>
                </div>
              )}
              {(e.state === 'TeacherDeclined' || e.state === 'AcceptanceExpired') && (
                <div
                  role="status"
                  style={{
                    background: '#f8f9fa',
                    border: '1px solid #ced4da',
                    borderRadius: 8,
                    padding: '10px 12px',
                    margin: '12px 0',
                    fontSize: 14,
                    color: '#495057',
                  }}
                >
                  {e.state === 'TeacherDeclined'
                    ? REQUEST_OUTCOME_COPY.declined
                    : REQUEST_OUTCOME_COPY.expired}
                </div>
              )}

              {/* Payment never completed — offer the way back in. */}
              {resumable && (
                <div
                  style={{
                    background: '#fff8e6',
                    border: '1px solid #f5a623',
                    borderRadius: 8,
                    padding: '10px 12px',
                    margin: '12px 0',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ flex: '1 1 240px', fontSize: 14, color: '#7a4d06' }}>
                    {e.state === 'PaymentFailed'
                      ? 'Tu pago fue rechazado, así que la inscripción no se completó.'
                      : 'El pago quedó pendiente, así que la inscripción no se completó.'}
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => handleResume(e)}
                  >
                    {busy ? 'Abriendo…' : 'Completar pago'}
                  </button>
                </div>
              )}

              {snapshotById[e.enrollmentId] && (
                <LearningProgressSummary snapshot={snapshotById[e.enrollmentId]} compact />
              )}

              {e.state === 'Active' && (
                <div style={{ fontSize: 14, color: '#444' }}>
                  Próxima sesión:{' '}
                  <strong>
                    {nextSessionLine(
                      tz,
                      snapshotById[e.enrollmentId],
                      studentsUnknown || unreadableLearners.has(e.childId),
                    )}
                  </strong>
                </div>
              )}
              {hb && <div style={{ fontSize: 14, color: '#e8590c', marginTop: 4 }}>{hb}</div>}

              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate(`/enrollments/${e.enrollmentId}`)}
                >
                  Ver detalle
                </button>
                {snapshotById[e.enrollmentId] && (
                  <Link
                    to={`/parent/progress?studentId=${encodeURIComponent(e.childId)}`}
                    className="btn btn-outline btn-sm"
                    style={{ textDecoration: 'none' }}
                  >
                    Ver progreso
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ParentDashboard;
